import React, { useMemo, useState } from "react";
import useSWR, { mutate } from "swr";
import { api } from "@/lib/api";
import { formatIDR } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const fetcher = (url) => api.get(url).then((r) => r.data);

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("id-ID", {
    day: "numeric",
    month: "short",
    year: "numeric"
  });
};

export default function History() {
  const { data: txs } = useSWR("/transactions", fetcher);
  const { data: accounts } = useSWR("/accounts", fetcher);
  const [q, setQ] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmId, setConfirmId] = useState(null);

  const accMap = useMemo(() => {
    const m = {};
    (accounts || []).forEach((a) => (m[a.id] = a.name));
    return m;
  }, [accounts]);

  const filtered = (txs || []).filter((t) => {
    if (filterType !== "all" && t.type !== filterType) return false;
    if (q) {
      const s = q.toLowerCase();
      return (
        t.category?.toLowerCase().includes(s) ||
        t.note?.toLowerCase().includes(s) ||
        accMap[t.account_id]?.toLowerCase().includes(s)
      );
    }
    return true;
  });

  const del = async (id) => {
    try {
      await api.delete(`/transactions/${id}`);
      toast.success("Transaksi dihapus");
      mutate("/transactions");
      mutate("/accounts");
      mutate("/reports/summary");
    } catch { toast.error("Gagal menghapus"); }
  };

  const handleConfirmDelete = () => {
    if (confirmId) {
      del(confirmId);
      setConfirmId(null);
    }
    setConfirmOpen(false);
  };

  return (
    <div className="animate-fade-up">
      <div className="flex items-baseline justify-between">
        <div>
          <p className="label-caps">Log</p>
          <h1 className="font-heading text-3xl md:text-4xl font-medium tracking-tight mt-1">Transaction History</h1>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mt-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Cari kategori, catatan, akun…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 h-11"
            data-testid="history-search"
          />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="h-11 sm:w-48" data-testid="history-filter-type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Tipe</SelectItem>
            <SelectItem value="income">Income</SelectItem>
            <SelectItem value="expense">Expense</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="mt-6 border border-border bg-card overflow-hidden">
        {/* Table header (only on desktop) */}
        <div className="hidden md:grid grid-cols-12 label-caps px-4 py-3 border-b border-border bg-secondary/40">
          <div className="col-span-3">Tanggal</div>
          <div className="col-span-3">Kategori</div>
          <div className="col-span-3">Akun</div>
          <div className="col-span-2 text-right">Jumlah</div>
          <div className="col-span-1"></div>
        </div>
        {filtered.length === 0 ? (
          <div className="p-10 text-center text-sm text-muted-foreground">Tidak ada transaksi.</div>
        ) : (
          filtered.map((t) => (
             <div key={t.id} className="flex flex-col md:grid md:grid-cols-12 px-4 py-4 md:py-3 border-b border-border last:border-0 items-start md:items-center hover:bg-accent/40 transition-colors gap-2 md:gap-0" data-testid={`tx-row-${t.id}`}>
               {/* Date */}
               <div className="col-span-3 text-xs md:text-sm text-muted-foreground md:text-foreground">{formatDate(t.date)}</div>
               {/* Category / Note */}
               <div className="col-span-3 text-sm w-full md:w-auto">
                 <div className="font-medium">{t.category}</div>
                 {t.note && <div className="text-xs text-muted-foreground truncate">{t.note}</div>}
               </div>
               {/* Account */}
               <div className="col-span-3 text-sm text-muted-foreground w-full md:w-auto">
                 <span className="inline md:hidden text-xs text-muted-foreground/60 mr-1.5 uppercase font-medium">Akun:</span>
                 {accMap[t.account_id] || "—"}
               </div>
               {/* Amount */}
               <div className={`col-span-2 md:text-right font-mono text-sm whitespace-nowrap w-full md:w-auto flex md:block justify-between items-center ${t.type === "income" ? "text-gain" : "text-loss"}`}>
                 <span className="inline md:hidden text-xs text-muted-foreground/60 mr-1.5 uppercase font-medium">Jumlah:</span>
                 <span>{t.type === "income" ? "+" : "-"}{formatIDR(t.amount)}</span>
               </div>
               {/* Actions */}
               <div className="col-span-1 text-right w-full md:w-auto border-t border-border/40 pt-2 md:pt-0 md:border-0 flex justify-end">
                 <Button variant="ghost" size="icon" onClick={() => { setConfirmId(t.id); setConfirmOpen(true); }} data-testid={`tx-delete-${t.id}`}>
                   <Trash2 className="h-4 w-4" />
                 </Button>
               </div>
            </div>
          ))
        )}
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Konfirmasi Hapus</DialogTitle>
          </DialogHeader>
          <div className="py-4 text-sm text-muted-foreground">
            Apakah Anda yakin ingin menghapus transaksi ini?
          </div>
          <DialogFooter className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>Batal</Button>
            <Button variant="destructive" className="bg-loss text-white hover:bg-loss/90" onClick={handleConfirmDelete}>Hapus</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
